"use client";
import {
  BOLD_CONTAINER_CLASS,
  BoldReviewsHeader,
  CarouselReviewFooter,
  GridReviewFooter,
  LeaveReviewButtons,
  ReviewRating,
  ReviewsTrack,
  SampleReviewsNote,
  SpotlightReview,
  WallReviewFooter,
} from "./review-parts";
import { MS_PER_MINUTE, reviewProofLabel, showsReviewCount } from "@servicepro/utils";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Card } from "../ui/card";
import { cn } from "../../lib/utils";
import { useSlideshow } from "../../lib/use-slideshow";
import type { BaseComponentProps, SectionStyle } from '../../types/puck';
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../ui/button";
import type { LeaveReviewLink } from "./review-parts";
import { describeReviewSources } from "./review-sources";

// Context for ServicePro config (will be provided by SSR wrapper or CSR app)
interface ServiceProConfig {
  apiUrl: string;
  siteId: string;
  slug: string;
  variant?: string;
  businessId?: string;
}

interface ReviewItem {
  name: string;
  location?: string;
  rating: number;
  text: string;
  date?: string;
  profileImageUrl?: string;
  // "servicepro" = direct feedback from the review-request landing page;
  // Every other value is a ReviewPlatform enum from the API.
  source?:
    | "google"
    | "yelp"
    | "facebook"
    | "thumbtack"
    | "nextdoor"
    | "bbb"
    | "angi"
    | "homeadvisor"
    | "other"
    | "servicepro";
}

/** A review as the public reviews endpoint returns it. */
interface ApiReview {
  profileImageUrl?: string | null;
  profileName?: string | null;
  rating: number;
  reviewText?: string | null;
  source?: ReviewItem["source"] | null;
  timeAgo?: string | null;
  timestamp?: string | null;
}

interface ReviewsResponse {
  reviews?: ApiReview[];
  reviewsPagePath?: string | null;
  sources?: string[];
  totalCount?: number;
}

interface ReviewsProps extends BaseComponentProps {
  heading: string;
  subheading?: string;
  reviews: ReviewItem[];
  layout: "grid" | "carousel" | "wall" | "spotlight";
  /** Bold: the heading in display capitals, to match the page's other bold sections. */
  variant?: SectionStyle;
  showRatings: "yes" | "no";
  showDates: "yes" | "no";
  backgroundColor?: string;
  useRealReviews?: "yes" | "no"; // New prop to fetch real reviews
  reviewLimit?: number; // Number of reviews to display
  minRating?: number; // Minimum star rating to display (1-5)
  /** Passed by Puck config render to avoid iframe timing issues with window.__SERVICEPRO_CONFIG__ */
  puckSiteId?: string;
  puckApiUrl?: string;
  /** The business profile's Google review count, shown until the API's all-platform total loads. */
  totalReviewCount?: number;
  /** The business's Google rating: under 30 reviews the badge says "4.7-Star Rated" instead of the count. */
  rating?: number;
  /**
   * The section is placed on every generated page and gated here, so a first
   * review shows up without regenerating. Outside the editor, no reviews means
   * no section; in the editor, sample reviews stand in with a note saying so.
   */
  isEditing?: boolean;
  /** Where a customer can leave a review. Shown on the site's reviews page only. */
  leaveReviewLinks?: LeaveReviewLink[];
}

const NO_LEAVE_REVIEW_LINKS: LeaveReviewLink[] = [];

// Create a context that can be imported from external packages
const ServiceProConfigContext = createContext<ServiceProConfig | undefined>(undefined),

  // Global cache to prevent duplicate fetches across component instances
  reviewsCache = new Map<
      string,
      {
        reviews: ApiReview[];
        totalCount: number;
        sources: string[];
        reviewsPagePath: string | null;
        timestamp: number;
      }
    >(),
  CACHE_TTL_MINUTES = 5,
  CACHE_TTL = CACHE_TTL_MINUTES * MS_PER_MINUTE,
  DEFAULT_REVIEW_LIMIT = 6,
  DEFAULT_MIN_RATING = 4,
  FULL_RATING = 5,
  /**
   * Bold cards are white, where the accent yellow measures 1.5:1; the stars take
   * the ink instead. The summary chip keeps its yellow stars on its dark ground.
   */
  CARD_STARS_ON_BOLD = "jobsite:text-foreground",
  PLACEHOLDER_REVIEWS: ReviewItem[] = [
      {
        date: "Jan 15, 2024",
        name: "Sarah Johnson",
        rating: 5,
        text: "Exceptional service! They arrived on time, diagnosed the problem quickly, and had it fixed within an hour. Professional and courteous.",
      },
      {
        date: "Jan 10, 2024",
        name: "Michael Chen",
        rating: 5,
        text: "Best decision we made was signing up for their maintenance plan. No more surprise breakdowns and energy bills have decreased significantly.",
      },
      {
        date: "Jan 5, 2024",
        name: "Jennifer Martinez",
        rating: 5,
        text: "Called them for an emergency repair on a Sunday. They came within 2 hours and had everything fixed fast. Fair pricing and excellent work.",
      },
    ],
  NO_REVIEWS: ReviewItem[] = [],

  hasText = (value: string | null | undefined): value is string => Boolean(value),

  textOr = <Text extends string>(value: Text | null | undefined, fallback: Text): Text => {
    if (hasText(value)) {
      return value;
    }
    return fallback;
  },

  nonBlank = (value: string | null | undefined): string | undefined => {
    if (hasText(value)) {
      return value;
    }
    return undefined;
  },

  countOr = (count: number | undefined, fallback: number): number => {
    if (count !== undefined && count !== 0) {
      return count;
    }
    return fallback;
  },

  apiReviewDate = (review: ApiReview): string | undefined => {
    if (hasText(review.timeAgo)) {
      return review.timeAgo;
    }
    if (hasText(review.timestamp)) {
      return new Date(review.timestamp).toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    }
    return undefined;
  },

  toReviewItem = (review: ApiReview): ReviewItem => ({
    date: apiReviewDate(review),
    location: undefined,
    name: textOr(review.profileName, "Anonymous"),
    profileImageUrl: nonBlank(review.profileImageUrl),
    rating: Math.round(review.rating),
    source: textOr(review.source, "google"),
    text: textOr(review.reviewText, ""),
  }),

  defaultReviewsFor = (reviews: ReviewItem[], isEditing: boolean): ReviewItem[] => {
    if (Array.isArray(reviews) && reviews.length > 0) {
      return reviews;
    }
    if (isEditing) {
      return PLACEHOLDER_REVIEWS;
    }
    return NO_REVIEWS;
  },

  windowServiceProConfig = (): ServiceProConfig | undefined => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const config: ServiceProConfig | undefined = (window as any).__SERVICEPRO_CONFIG__;
    return config;
  },

  firstText = (preferred: string | undefined, fallback: string | undefined): string | undefined => {
    if (hasText(preferred)) {
      return preferred;
    }
    return fallback;
  },

  hasBody = (review: ReviewItem): boolean => hasText(review.text?.trim()),

  // A class for a review with text, or the one for a rating-only review.
  classForText = (review: ReviewItem, withText: string, withoutText: string): string => {
    if (hasBody(review)) {
      return withText;
    }
    return withoutText;
  },

  toggleLabel = (expanded: boolean): string => {
    if (expanded) {
      return "Show less";
    }
    return "Read more";
  },

  expandStep = (expanded: boolean): number => {
    if (expanded) {
      return 1;
    }
    return -1;
  },

  viewAllPathFor = (reviewsPagePath: string | null, onReviewsPage: boolean): string | null => {
    if (hasText(reviewsPagePath) && !onReviewsPage) {
      return reviewsPagePath;
    }
    return null;
  },

  sourcesLineFor = (useRealReviews: "yes" | "no", sources: string[]): string | null => {
    if (useRealReviews === "yes") {
      return describeReviewSources(sources);
    }
    return null;
  },

  dotClass = (active: boolean): string => {
    if (active) {
      return "jobsite:bg-accent jobsite:w-8";
    }
    return "jobsite:bg-muted-foreground/30 jobsite:hover:bg-muted-foreground/50";
  },

  countPrefix = (count: number): string => {
    // A small count makes the business look small; the link just says "View all reviews".
    if (showsReviewCount(count)) {
      return `${count}+ `;
    }
    return "";
  },

  /**
   * The average of the reviews shown, for a business with no Google rating on file.
   * @param {{ rating?: number }[]} shown The reviews on the page.
   * @returns {number | undefined} The average, or undefined with none rated.
   */
  averageRating = (shown: { rating?: number }[]): number | undefined => {
    const rated = shown.map((review) => review.rating).filter((value): value is number => typeof value === "number" && value > 0);
    return rated.length === 0 ? undefined : rated.reduce((sum, value) => sum + value, 0) / rated.length;
  },

  // Review text with a "Read more" toggle that appears only when the text is
  // Actually clamped (grid and carousel cap at 6 lines; long reviews overflow).
  ReviewText = ({
    text,
    wrapperClassName,
    textClassName,
    onExpandChange,
  }: {
    text: string;
    wrapperClassName?: string;
    textClassName: string;
    onExpandChange?: (expanded: boolean) => void;
  }): React.JSX.Element => {
    const [expanded, setExpanded] = useState(false),
      [isClamped, setIsClamped] = useState(false),
      textRef = useRef<HTMLParagraphElement>(null);

    useEffect(() => {
      const el = textRef.current;
      if (!el) {
        return;
      }
      setIsClamped(el.scrollHeight > el.clientHeight + 1);
    }, [text]);

    return (
      <div className={wrapperClassName}>
        <p ref={textRef} className={cn(textClassName, !expanded && "jobsite:line-clamp-6")}>
          &ldquo;{text}&rdquo;
        </p>
        {(isClamped || expanded) && (
          <button
            type="button"
            onClick={() => {
              const next = !expanded;
              setExpanded(next);
              onExpandChange?.(next);
            }}
            className="jobsite:mt-2 jobsite:text-sm jobsite:font-semibold jobsite:text-primary jobsite:hover:underline"
          >
            {toggleLabel(expanded)}
          </button>
        )}
      </div>
    );
  },

  Reviews: React.FC<ReviewsProps> = ({
    heading,
    subheading,
    reviews: defaultReviewsProp,
    layout = "grid",
    showRatings = "yes",
    showDates = "yes",
    backgroundColor,
    leaveReviewLinks = NO_LEAVE_REVIEW_LINKS,
    useRealReviews = "yes",
    reviewLimit = DEFAULT_REVIEW_LIMIT,
    minRating = DEFAULT_MIN_RATING,
    puckSiteId,
    puckApiUrl,
    totalReviewCount: totalReviewCountProp,
    rating,
    isEditing = false,
    variant = "standard",
    id,
    className,
  }) => {
    const isBold = variant === "bold",
      defaultReviews = defaultReviewsFor(defaultReviewsProp, isEditing),
      [reviews, setReviews] = useState<ReviewItem[]>(defaultReviews),
      [, setIsLoading] = useState(false),
      // The business profile's Google count paints first; the API's total, which adds
      // Every other platform, replaces it once real reviews load.
      [totalReviewCount, setTotalReviewCount] = useState<number>(
        totalReviewCountProp ?? defaultReviews.length,
      ),
      [reviewSources, setReviewSources] = useState<string[]>([]),
      // Path of the site's /reviews page (from the API) for the "View all" link
      [reviewsPagePath, setReviewsPagePath] = useState<string | null>(null),
      // While a carousel review is expanded, pause auto-play so it doesn't slide away mid-read
      [expandedCount, setExpandedCount] = useState(0),
      fetchingRef = useRef(false),
      hasFetchedRef = useRef(false),

      // Try to get config from context first (SSR), then fall back to window (CSR)
      contextConfig = useContext(ServiceProConfigContext),
      windowConfig = windowServiceProConfig(),
      config = contextConfig || windowConfig,
      siteId = firstText(puckSiteId, config?.siteId),
      apiUrl = firstText(puckApiUrl, config?.apiUrl),

      // Reset fetch guard when key inputs change so re-fetching happens
      prevFetchKey = useRef<string | null>(null),
      fetchKey = `${useRealReviews}-${siteId}-${reviewLimit}-${minRating}`;

    // Fetch real reviews if enabled
    useEffect(() => {
      if (prevFetchKey.current !== null && prevFetchKey.current !== fetchKey) {
        hasFetchedRef.current = false;
      }
      prevFetchKey.current = fetchKey;

      // Don't fetch if real reviews aren't enabled
      if (useRealReviews !== "yes") {
        setReviews(defaultReviews);
        return;
      }

      // Don't fetch if we don't have the required config
      if (!hasText(siteId) || !hasText(apiUrl)) {
        setReviews(defaultReviews);
        return;
      }

      // Don't fetch if already fetching or already fetched for this key
      if (fetchingRef.current || hasFetchedRef.current) {
        return;
      }

      const cacheKey = `${siteId}-${reviewLimit}-${minRating}`,

        // Check cache first
        cached = reviewsCache.get(cacheKey),

        showReviews = (data: ReviewsResponse): void => {
          setReviewsPagePath(data.reviewsPagePath ?? null);

          if (data.reviews && data.reviews.length > 0) {
            // Cache the raw response
            reviewsCache.set(cacheKey, {
              reviews: data.reviews,
              reviewsPagePath: data.reviewsPagePath ?? null,
              sources: data.sources ?? [],
              timestamp: Date.now(),
              totalCount: countOr(data.totalCount, data.reviews.length),
            });

            // Format reviews for display
            setReviews(data.reviews.map(toReviewItem));
            setTotalReviewCount(countOr(data.totalCount, data.reviews.length));
            setReviewSources(data.sources ?? []);
          } else {
            // No real reviews found, use defaults
            setReviews(defaultReviews);
            setReviewSources([]);
            if (totalReviewCountProp === undefined) {
              setTotalReviewCount(defaultReviews.length);
            }
          }
        },

        fetchReviews = async (): Promise<void> => {
          fetchingRef.current = true;

          try {
            setIsLoading(true);

            const url = `${apiUrl}/api/sites/public/${siteId}/reviews?limit=${reviewLimit}&minRating=${minRating}`,
              response = await fetch(url);

            if (!response.ok) {
              throw new Error("Failed to fetch reviews");
            }

            showReviews(await response.json());
          } catch {
            // Handled state, not a crash — fall back to the defaults rather than
            // Logging into a visitor's console.
            setReviews(defaultReviews);
            setReviewSources([]);
          } finally {
            setIsLoading(false);
            fetchingRef.current = false;
            hasFetchedRef.current = true;
          }
        };

      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        const formattedReviews = cached.reviews.map(toReviewItem);
        setReviews(formattedReviews);
        setTotalReviewCount(cached.totalCount);
        setReviewSources(cached.sources);
        setReviewsPagePath(cached.reviewsPagePath);
        hasFetchedRef.current = true;
        return;
      }

      fetchReviews();
    }, [fetchKey, useRealReviews, siteId, apiUrl, reviewLimit, minRating, defaultReviews, totalReviewCountProp]);

    // Auto-play carousel (paused while a review is expanded)
    const {
        index: currentIndex,
        next: nextSlide,
        prev: prevSlide,
        goTo: goToSlide,
      } = useSlideshow({
        count: reviews.length,
        intervalMs: 5000,
        paused: layout !== "carousel" || expandedCount > 0,
      }),
      showingPlaceholders = reviews === PLACEHOLDER_REVIEWS,

      handleExpandChange = (expanded: boolean): void => {
        setExpandedCount((count) => count + expandStep(expanded));
      },

      // "View all" links to the site's /reviews page when one exists, except when
      // The widget is already rendered on that page.
      onReviewsPage =
        reviewsPagePath !== null &&
        typeof window !== "undefined" &&
        window.location.pathname.replace(/\/+$/u, "") === reviewsPagePath,
      viewAllPath = viewAllPathFor(reviewsPagePath, onReviewsPage),
      leaveLinks = onReviewsPage ? leaveReviewLinks : NO_LEAVE_REVIEW_LINKS,
      surfaceRadiusStyle = { borderRadius: "var(--jobsite-radius)" } as const,
      sourcesLine = sourcesLineFor(useRealReviews, reviewSources),
      // Google's rating when the business has one, else the average of the reviews shown.
      shownRating = rating ?? averageRating(reviews),
      proofLabel = reviewProofLabel({ count: totalReviewCount, rating: shownRating }),
      isSpotlight = layout === "spotlight" && reviews.length > 0,
      isWall = layout === "wall",
      isGrid = layout === "grid",
      isCarousel = !isSpotlight && !isWall && !isGrid;

    if (reviews.length === 0) {
      // A business with no reviews yet needs these links the most.
      if (leaveLinks.length === 0) {
        return null;
      }
      return (
        <section id={id} className={cn("jobsite:py-16 jobsite:md:py-24", className)}>
          <div className={isBold ? BOLD_CONTAINER_CLASS : "jobsite:container jobsite:mx-auto jobsite:px-4"}>
            <LeaveReviewButtons align={isBold ? "start" : "center"} links={leaveLinks} />
          </div>
        </section>
      );
    }

    return (
      <section
        id={id}
        className={cn(
          "jobsite:py-16 jobsite:md:py-24",
          { "jobsite:bg-muted": !hasText(backgroundColor) },
          className,
        )}
        style={{
          backgroundColor: nonBlank(backgroundColor),
        }}
      >
        <div className={isBold ? BOLD_CONTAINER_CLASS : "jobsite:container jobsite:mx-auto jobsite:px-4"}>
          {isBold ? (
            <BoldReviewsHeader
              heading={heading}
              subheading={hasText(subheading) ? subheading : undefined}
              showSampleNote={showingPlaceholders}
            />
          ) : (
          <div className="jobsite:text-center jobsite:mb-12 jobsite:max-w-3xl jobsite:mx-auto">
            <h2 className="jobsite:text-3xl jobsite:sm:text-4xl jobsite:md:text-5xl jobsite:mb-4 jobsite:font-heading jobsite:tracking-tight jobsite:text-balance jobsite:text-foreground">
              {heading}
            </h2>
            {hasText(subheading) && (
              <p className="jobsite:text-lg jobsite:md:text-xl jobsite:font-body jobsite:text-pretty jobsite:text-muted-foreground">
                {subheading}
              </p>
            )}
            {showingPlaceholders && <SampleReviewsNote />}
          </div>
          )}

          {((showRatings === "yes" && reviews.length > 0) || hasText(sourcesLine)) && (
            <div
              className={
                isBold
                  ? "jobsite:flex jobsite:flex-col jobsite:items-start jobsite:gap-3 jobsite:mb-10"
                  : "jobsite:flex jobsite:flex-col jobsite:items-center jobsite:gap-3 jobsite:mb-10"
              }
            >
              {showRatings === "yes" && reviews.length > 0 && proofLabel !== null && (
                <div
                  className={cn(
                    "jobsite:inline-flex jobsite:items-center jobsite:gap-3 jobsite:px-6 jobsite:py-3",
                    // On bold, a dark chip: yellow stars only read on a dark ground.
                    isBold ? "jobsite:bg-primary" : "jobsite:bg-accent/10 jobsite:border jobsite:border-accent/20",
                  )}
                  style={surfaceRadiusStyle}
                >
                  <ReviewRating
                    className="jobsite:text-accent jobsite:flex jobsite:gap-0.5"
                    innerClassName="jobsite:flex jobsite:gap-1"
                    rating={shownRating ?? FULL_RATING}
                    starClassName="jobsite:w-5 jobsite:h-5"
                  />
                  <span className={cn("jobsite:text-sm jobsite:font-semibold", isBold ? "jobsite:text-primary-foreground" : "jobsite:text-foreground")}>
                    {/* The count once it is impressive; below 30 the rating instead (reviewProofLabel). */}
                    {proofLabel}
                  </span>
                </div>
              )}
              {hasText(sourcesLine) && (
                <p
                  className={
                    isBold
                      ? "jobsite:text-sm jobsite:font-body jobsite:text-muted-foreground"
                      : "jobsite:text-sm jobsite:text-center jobsite:font-body jobsite:text-muted-foreground"
                  }
                >
                  {sourcesLine}
                </p>
              )}
            </div>
          )}

          {leaveLinks.length > 0 && (
            <LeaveReviewButtons align={isBold ? "start" : "center"} className="jobsite:mb-10" links={leaveLinks} />
          )}

          {isSpotlight && <SpotlightReview review={reviews[0]} showDates={showDates} />}

          {isWall && (
            <div className="jobsite:columns-1 jobsite:sm:columns-2 jobsite:lg:columns-3 jobsite:gap-4">
              {reviews.map((review, idx) => (
                <Card
                  key={idx}
                  className="jobsite:mb-4 jobsite:break-inside-avoid jobsite:p-5 jobsite:relative jobsite:transition-colors jobsite:duration-200 jobsite:border jobsite:border-border/70 jobsite:hover:border-accent/40 jobsite:shadow-sm"
                >
                  {showRatings === "yes" && (
                    <ReviewRating
                      className={cn(
                        "jobsite:flex jobsite:items-center jobsite:gap-1.5",
                        classForText(review, "jobsite:mb-3", "jobsite:mb-4 jobsite:justify-center jobsite:py-2"),
                      )}
                      innerClassName={cn("jobsite:text-accent jobsite:flex jobsite:gap-0.5", isBold && CARD_STARS_ON_BOLD)}
                      rating={review.rating}
                      starClassName={classForText(review, "jobsite:w-3.5 jobsite:h-3.5", "jobsite:w-6 jobsite:h-6")}
                    />
                  )}

                  {hasBody(review) && (
                    <p className="jobsite:text-sm jobsite:leading-relaxed jobsite:mb-4 jobsite:font-body jobsite:text-card-foreground">
                      &ldquo;{review.text}&rdquo;
                    </p>
                  )}

                  <WallReviewFooter review={review} showDates={showDates} />
                </Card>
              ))}
            </div>
          )}

          {isGrid && (
            <div className="jobsite:grid jobsite:md:grid-cols-2 jobsite:lg:grid-cols-3 jobsite:gap-5">
              {reviews.map((review, idx) => (
                <Card
                  key={idx}
                  className="jobsite:flex jobsite:flex-col jobsite:p-7 jobsite:md:p-8 jobsite:relative jobsite:transition-all jobsite:duration-200 jobsite:border jobsite:border-border/70 jobsite:hover:-translate-y-0.5 jobsite:hover:border-accent/30 jobsite:hover:shadow-md jobsite:shadow-sm"
                >
                  {showRatings === "yes" && (
                    <ReviewRating
                      className={cn(
                        "jobsite:text-accent",
                        classForText(review, "jobsite:mb-4", "jobsite:mb-6 jobsite:flex-1 jobsite:flex jobsite:items-center jobsite:justify-center"),
                        isBold && CARD_STARS_ON_BOLD,
                      )}
                      innerClassName="jobsite:flex jobsite:gap-1"
                      rating={review.rating}
                      starClassName={classForText(review, "jobsite:w-5 jobsite:h-5", "jobsite:w-8 jobsite:h-8")}
                    />
                  )}

                  {hasBody(review) && (
                    <ReviewText
                      text={review.text}
                      wrapperClassName="jobsite:flex-1 jobsite:mb-6"
                      textClassName="jobsite:text-base jobsite:md:text-lg jobsite:leading-relaxed jobsite:font-body jobsite:text-card-foreground"
                    />
                  )}

                  <GridReviewFooter review={review} showDates={showDates} />
                </Card>
              ))}
            </div>
          )}

          {isCarousel && (
            <div className="jobsite:relative jobsite:max-w-3xl jobsite:mx-auto">
              <ReviewsTrack currentIndex={currentIndex}>
                {reviews.map((review, idx) => (
                  <div key={idx} className="jobsite:w-full jobsite:shrink-0 jobsite:px-2">
                    <Card className="jobsite:p-8 jobsite:md:p-10 jobsite:relative jobsite:h-full jobsite:flex jobsite:flex-col jobsite:border jobsite:border-border/70 jobsite:shadow-lg">
                      {showRatings === "yes" && (
                        <ReviewRating
                          className={cn(
                            "jobsite:text-accent",
                            classForText(review, "jobsite:mb-4", "jobsite:mb-8 jobsite:flex-1 jobsite:flex jobsite:items-center jobsite:justify-center jobsite:py-4"),
                            isBold && CARD_STARS_ON_BOLD,
                          )}
                          innerClassName="jobsite:flex jobsite:gap-1"
                          rating={review.rating}
                          starClassName={classForText(review, "jobsite:w-5 jobsite:h-5", "jobsite:w-10 jobsite:h-10")}
                        />
                      )}

                      {hasBody(review) && (
                        <ReviewText
                          text={review.text}
                          wrapperClassName="jobsite:flex-1 jobsite:mb-8"
                          textClassName="jobsite:text-lg jobsite:md:text-xl jobsite:leading-relaxed jobsite:font-body jobsite:text-card-foreground"
                          onExpandChange={handleExpandChange}
                        />
                      )}

                      <CarouselReviewFooter review={review} showDates={showDates} />
                    </Card>
                  </div>
                ))}
              </ReviewsTrack>

              {reviews.length > 1 && (
                <>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={prevSlide}
                    className="jobsite:absolute jobsite:left-0 jobsite:top-1/2 jobsite:-translate-y-1/2 jobsite:-translate-x-4 jobsite:z-10 jobsite:h-11 jobsite:w-11 jobsite:rounded-full jobsite:bg-background/90 jobsite:shadow-lg jobsite:hover:bg-accent jobsite:hover:text-accent-foreground jobsite:border-2"
                    aria-label="Previous review"
                  >
                    <ChevronLeft className="jobsite:h-6 jobsite:w-6" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={nextSlide}
                    className="jobsite:absolute jobsite:right-0 jobsite:top-1/2 jobsite:-translate-y-1/2 jobsite:translate-x-4 jobsite:z-10 jobsite:h-11 jobsite:w-11 jobsite:rounded-full jobsite:bg-background/90 jobsite:shadow-lg jobsite:hover:bg-accent jobsite:hover:text-accent-foreground jobsite:border-2"
                    aria-label="Next review"
                  >
                    <ChevronRight className="jobsite:h-6 jobsite:w-6" />
                  </Button>
                </>
              )}

              {reviews.length > 1 && (
                <div className="jobsite:flex jobsite:justify-center jobsite:gap-2 jobsite:mt-8">
                  {reviews.map((_review, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        goToSlide(idx);
                      }}
                      className={cn(
                        "jobsite:w-3 jobsite:h-3 jobsite:rounded-full jobsite:transition-all jobsite:duration-300",
                        dotClass(currentIndex === idx),
                      )}
                      aria-label={`Go to review ${idx + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {hasText(viewAllPath) && (
            <div className="jobsite:mt-10 jobsite:text-center">
              <a
                href={viewAllPath}
                className="jobsite:inline-flex jobsite:items-center jobsite:gap-1.5 jobsite:font-semibold jobsite:text-primary jobsite:hover:underline"
              >
                View all {countPrefix(totalReviewCount)}reviews
                <ChevronRight className="jobsite:h-4 jobsite:w-4" />
              </a>
            </div>
          )}
        </div>
      </section>
    );
  };

export type { ReviewItem, ReviewsProps };
export { ServiceProConfigContext, Reviews };
